"use client";

import {createContext, useContext, useMemo, type ReactNode} from "react";
import {isPresentationRecord, presentationValue, type ToolPresentation} from "@repo/ui/gailgpt/tool-presentation";
import {useTranslation} from "react-i18next";
import {usePhoneScriptNames} from "@/hooks/usePhoneScriptNames";
import {useTextScriptNames} from "@/hooks/useTextScriptNames";
import {useToolNames} from "@/hooks/useToolNames";
import {useApprovalEntityName} from "@/hooks/useApprovalEntityName";
import {useApprovalUseCaseNames} from "@/hooks/useApprovalUseCaseNames";
import {ApprovalLookupBudgetProvider} from "@/hooks/ApprovalLookupBudget";
import {frozenReferenceNames, resolveApprovalReference, type ApprovalReference, type ApprovalReferenceContext, type ApprovalReferenceKind} from "./approvalReferences";

const ReferenceContext = createContext<ApprovalReferenceContext>({names: new Map(), paths: new Map()});
export const useApprovalReferences = () => useContext(ReferenceContext);

export function ApprovalReferenceProvider({presentation, parameters, children}: {presentation: ToolPresentation; parameters: Record<string, unknown>; children: ReactNode}) {
   const value = useMemo(() => {
      const paths = new Map<string, {id: string; name: string}>();
      for (const field of presentation.fields) {
         const {value: id} = presentationValue(parameters, field.path);
         if (field.format === "entity" && field.entityName && typeof id === "string") paths.set(JSON.stringify(field.path), {id, name: field.entityName});
      }
      const scriptId = parameters.scriptId ?? (isPresentationRecord(parameters.body) ? parameters.body.scriptId : undefined);
      return {toolName: presentation.toolName, scriptId: typeof scriptId === "string" ? scriptId : undefined, names: frozenReferenceNames(presentation, parameters), paths};
   }, [presentation, parameters]);
   return <ApprovalLookupBudgetProvider><ReferenceContext.Provider value={value}>{children}</ReferenceContext.Provider></ApprovalLookupBudgetProvider>;
}

function Label({id, name}: {id: string; name?: string}) {
   return <span title={id} className="whitespace-pre-wrap [overflow-wrap:anywhere]">{name || id}</span>;
}
function ScriptName({id}: {id: string}) {
   const names = usePhoneScriptNames(true);
   return <Label id={id} name={names.get(id.toLowerCase())} />;
}
function TextScriptName({id}: {id: string}) {
   const names = useTextScriptNames(true);
   return <Label id={id} name={names.get(id.toLowerCase())} />;
}
function ToolName({id}: {id: string}) {
   const names = useToolNames(true);
   return <Label id={id} name={names.get(id)} />;
}
function EntityName({id, kind}: {id: string; kind: Exclude<ApprovalReferenceKind, "script" | "textScript" | "tool">}) {
   const {t} = useTranslation("knowledge");
   const {scriptId} = useApprovalReferences();
   const name = useApprovalEntityName(kind, id, kind === "eval" ? scriptId : undefined);
   return <Label id={id} name={name || (kind === "document" ? t("manageTagsModal.genericName") : undefined)} />;
}

/** Frozen labels take priority, then a typed read; the ID remains the sole execution value. */
export default function ApprovalReferenceValue(reference: ApprovalReference) {
   const context = useApprovalReferences();
   const resolution = resolveApprovalReference(reference, context);
   const {id} = reference;
   if (resolution.status !== "lookup") return <Label id={id} name={resolution.status === "resolved" ? resolution.name : undefined} />;
   const {kind} = resolution;
   if (kind === "script") return <ScriptName id={id} />;
   if (kind === "textScript") return <TextScriptName id={id} />;
   if (kind === "tool") return <ToolName id={id} />;
   return <EntityName id={id} kind={kind} />;
}

/** Collection summaries and cells use the same precedence as individual references. */
export function useApprovalAssociationNames(references: Omit<ApprovalReference, "kind">[]) {
   const context = useApprovalReferences();
   const resolutions = references.map(reference => resolveApprovalReference({...reference, kind: "useCase"}, context));
   const names = useApprovalUseCaseNames(references.filter((_, index) => resolutions[index].status === "lookup").map(reference => reference.id));
   return resolutions.map((resolution, index) => {
      if (resolution.status === "resolved") return resolution.name;
      const id = references[index].id;
      const name = resolution.status === "lookup" ? names?.get(id) : undefined;
      return name || id;
   });
}
